import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { getStatusBadgeColor } from './dashboardUtils';

const StatusDropdown = ({ 
  currentStatus, 
  onStatusChange, 
  projectId, 
  size = 'sm',
  isUpdating = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const statusOptions = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleStatusSelect = async (newStatus) => {
    if (newStatus === currentStatus || isUpdating) return;
    
    setIsOpen(false);
    try {
      await onStatusChange(projectId, newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUpdating) {
      setIsOpen(!isOpen);
    }
  };

  const buttonClasses = size === 'lg' 
    ? 'px-3 py-1 text-sm'
    : 'px-2 py-0.5 text-xs';

  const dropdownClasses = size === 'lg'
    ? 'py-1 text-sm min-w-[140px]'
    : 'py-1 text-xs min-w-[120px]';

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        disabled={isUpdating}
        className={`
          ${buttonClasses} 
          ${getStatusBadgeColor(currentStatus)} 
          border rounded-full font-medium inline-flex items-center space-x-1
          hover:brightness-110 transition-all duration-150
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-blue-500/50
        `}
      >
        <span>{currentStatus.replace('_', ' ')}</span>
        {!isUpdating && (
          <ChevronDown 
            className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          />
        )}
        {isUpdating && (
          <div className="w-3 h-3">
            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </button>

      {isOpen && (
        <div className={`
          absolute z-50 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl
          backdrop-blur-sm transform transition-all duration-150 origin-top
          opacity-100 scale-100
          ${dropdownClasses}
          ${size === 'lg' ? 'top-full' : 'top-full'}
        `}>
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusSelect(option.value)}
              className={`
                w-full px-3 py-2 text-left hover:bg-slate-700/50 transition-colors
                flex items-center space-x-2 first:rounded-t-lg last:rounded-b-lg
                ${option.value === currentStatus ? 'bg-slate-700/30' : ''}
              `}
            >
              <div className={`w-2 h-2 rounded-full ${getStatusBadgeColor(option.value).split(' ')[0]}`} />
              <span className="text-slate-200">{option.label}</span>
              {option.value === currentStatus && (
                <span className="ml-auto text-blue-400 text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;